import React, { useMemo, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { L, tr } from "@/lib/i18n";
import { statusPill } from "./statusUtil";
import { visibleUnitFields, formatFieldValue, fieldLabel } from "@/lib/unitFinder";

/**
 * UnitsListPanel — the "Units" mode of the public viewer.
 *
 * iter30: switched from a flat per-Block sidebar to the
 * Project → Etap → Block hierarchy.
 *
 * Scope state shape:
 *   { type: "all" }                       — every unit
 *   { type: "etap",  id: "<etap.id>" }   — every unit under that etap
 *   { type: "block", id: "<block.id>" }  — only that block
 *
 * Each scope shows totals + available counts in the sidebar so a sales
 * agent can see at a glance which Etap still has stock.
 */
export default function UnitsList({ inventory, project, onClose, onPickUnit, onLocate, lang, defaultLang, initialEtapCode, state = {}, onStateChange, fieldDefinitions = [], unitsWithPanorama = new Set() }) {
  const { etaps, blocks, blocksByEtap, blockById, etapById, orphanBlocks } = useMemo(() => {
    const all = inventory.groups || [];
    const etapsArr = all
      .filter((g) => g.group_type === "etap")
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const blocksArr = all.filter((g) => g.group_type === "block");
    // Legacy projects (pre-iter30) only have blocks with no group_type set.
    // Treat anything that isn't an etap as a block so the list never goes
    // empty on un-migrated content.
    const looseBlocks = all.filter((g) => !g.group_type && g.parent_group_id == null);
    const allBlocks = [...blocksArr, ...looseBlocks].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const map = new Map();
    for (const b of allBlocks) {
      const k = b.parent_group_id || "_orphan";
      const arr = map.get(k) || [];
      arr.push(b);
      map.set(k, arr);
    }
    return {
      etaps: etapsArr,
      blocks: allBlocks,
      blocksByEtap: map,
      blockById: Object.fromEntries(allBlocks.map((b) => [b.id, b])),
      etapById: Object.fromEntries(etapsArr.map((e) => [e.id, e])),
      orphanBlocks: map.get("_orphan") || [],
    };
  }, [inventory.groups]);

  // Default scope is "All units" — sales agents asked to see the full
  // inventory first and narrow from there, rather than landing on an
  // arbitrary first block. Matches the breadcrumb expectation in the
  // public viewer test plan. If a sharable per-Etap URL is in play, we
  // pre-select that Etap (resolved by stable code, case-insensitive).
  const initialScope = useMemo(() => {
    if (!initialEtapCode) return { type: "all" };
    const match = etaps.find(
      (e) => (e.code || "").trim().toLowerCase() === String(initialEtapCode).trim().toLowerCase(),
    );
    return match ? { type: "etap", id: match.id } : { type: "all" };
  }, [initialEtapCode, etaps]);
  const [localState, setLocalState] = useState(state);
  const currentState = onStateChange ? state : localState;
  const updateState = (patch) => { const next = { ...currentState, ...patch }; onStateChange ? onStateChange(next) : setLocalState(next); };
  const scope = currentState.scope || initialScope;
  const setScope = scope => updateState({ scope });
  const statusFilter = currentState.statusFilter || "all";
  const setStatusFilter = statusFilter => updateState({ statusFilter });
  // Etaps the user has explicitly collapsed; expanded by default so the
  // tree behaves like a "show me everything" sales reference, not a puzzle.
  const collapsedEtaps = currentState.collapsedEtaps || {};
  const setCollapsedEtaps = fn => updateState({ collapsedEtaps: fn(collapsedEtaps) });

  const inScope = (e) => {
    if (scope.type === "all") return true;
    if (scope.type === "block") return e.group_id === scope.id;
    if (scope.type === "etap") {
      const b = blockById[e.group_id];
      return !!b && b.parent_group_id === scope.id;
    }
    return false;
  };
  const list = inventory.entities
    .filter(inScope)
    .filter((e) => statusFilter === "all" || e.status === statusFilter);

  const countsForBlock = (blockId) => {
    const ents = inventory.entities.filter((e) => e.group_id === blockId);
    return { total: ents.length, avail: ents.filter((e) => e.status === "available").length };
  };
  const countsForEtap = (etapId) => {
    const bs = blocksByEtap.get(etapId) || [];
    const ids = new Set(bs.map((b) => b.id));
    const ents = inventory.entities.filter((e) => ids.has(e.group_id));
    return { total: ents.length, avail: ents.filter((e) => e.status === "available").length };
  };
  const overallCounts = (() => {
    const ents = inventory.entities;
    return { total: ents.length, avail: ents.filter((e) => e.status === "available").length };
  })();

  // Breadcrumb for the active scope.
  const crumb = (() => {
    if (scope.type === "all") return [L(lang, "all_units") || "All units"];
    if (scope.type === "etap") {
      const e = etapById[scope.id];
      return [tr(e, "name", lang, defaultLang) || e?.name || "—"];
    }
    if (scope.type === "block") {
      const b = blockById[scope.id];
      const e = b ? etapById[b.parent_group_id] : null;
      const parts = [];
      if (e) parts.push(tr(e, "name", lang, defaultLang) || e.name);
      if (b) parts.push(tr(b, "name", lang, defaultLang) || b.name);
      return parts.length ? parts : ["—"];
    }
    return ["—"];
  })();

  return (
    <div className="absolute inset-0 z-40 viewer-shell fade-in flex flex-col" data-testid="units-list-panel">
      <div className="px-6 md:px-10 pt-6 pb-4 flex flex-wrap gap-3 items-center justify-between border-b border-white/10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">{tr(project, "name", lang, defaultLang) || project.name}</div>
          <div className="font-display text-3xl text-white tracking-tight mt-1">{L(lang, "units")}</div>
        </div>
        <button onClick={onClose} className="pill-nav text-white text-xs uppercase tracking-wider px-4 py-2 rounded-full inline-flex items-center gap-2" data-testid="units-list-close">
          <X className="h-4 w-4" /> {L(lang, "back_to_tour")}
        </button>
      </div>

      <div className="flex flex-col md:flex-row flex-1 min-h-0">
        <aside className="w-full max-h-[28vh] md:max-h-none md:w-64 shrink-0 border-r border-white/10 px-3 py-5 overflow-auto scrollbar-thin">
          <ScopeButton
            label={L(lang, "all_units") || "All units"}
            active={scope.type === "all"}
            onClick={() => setScope({ type: "all" })}
            avail={overallCounts.avail}
            total={overallCounts.total}
            testid="scope-all"
          />

          {etaps.length === 0 && orphanBlocks.length === 0 && (
            <div className="text-white/40 text-sm px-3 mt-4" data-testid="units-empty">—</div>
          )}

          {etaps.map((etap) => {
            const bs = blocksByEtap.get(etap.id) || [];
            const counts = countsForEtap(etap.id);
            const isCollapsed = !!collapsedEtaps[etap.id];
            const etapName = tr(etap, "name", lang, defaultLang) || etap.name;
            return (
              <div key={etap.id} className="mt-3" data-testid={`etap-section-${etap.id}`}>
                <div className="flex items-center gap-1 px-2">
                  <button
                    onClick={() => setCollapsedEtaps((s) => ({ ...s, [etap.id]: !isCollapsed }))}
                    className="p-1 text-white/50 hover:text-white"
                    aria-label={L(lang, isCollapsed ? "expand" : "collapse")}
                    data-testid={`etap-toggle-${etap.id}`}
                  >
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
                  </button>
                  <button
                    onClick={() => setScope({ type: "etap", id: etap.id })}
                    className={`flex-1 text-left px-1.5 py-1 rounded text-[10px] uppercase tracking-[0.25em] transition ${scope.type === "etap" && scope.id === etap.id ? "text-white" : "text-white/55 hover:text-white"}`}
                    data-testid={`etap-tab-${etap.id}`}
                  >
                    <span className="truncate">{etapName}</span>
                  </button>
                  <span className="text-[10px] text-white/40">{counts.avail}/{counts.total}</span>
                </div>
                {!isCollapsed && (
                  <div className="mt-1 space-y-0.5">
                    {bs.length === 0 && <div className="text-white/35 text-[11px] px-7 py-1">—</div>}
                    {bs.map((b) => {
                      const bc = countsForBlock(b.id);
                      const active = scope.type === "block" && scope.id === b.id;
                      return (
                        <button
                          key={b.id}
                          onClick={() => setScope({ type: "block", id: b.id })}
                          data-testid={`block-tab-${b.id}`}
                          className={`w-full text-left pl-7 pr-3 py-2 rounded-md text-sm transition flex items-center justify-between gap-2 ${active ? "bg-white/12 text-white" : "text-white/70 hover:bg-white/5"}`}
                        >
                          <span className="truncate">{tr(b, "name", lang, defaultLang) || b.name}</span>
                          <span className="text-[10px] text-white/50 shrink-0">{bc.avail}/{bc.total}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {orphanBlocks.length > 0 && (
            <div className="mt-5" data-testid="orphan-blocks-section">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/35 px-2 mb-1">{L(lang, "blocks") || "Blocks"}</div>
              <div className="space-y-0.5">
                {orphanBlocks.map((b) => {
                  const bc = countsForBlock(b.id);
                  const active = scope.type === "block" && scope.id === b.id;
                  return (
                    <button
                      key={b.id}
                      onClick={() => setScope({ type: "block", id: b.id })}
                      data-testid={`block-tab-${b.id}`}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition flex items-center justify-between gap-2 ${active ? "bg-white/12 text-white" : "text-white/70 hover:bg-white/5"}`}
                    >
                      <span className="truncate">{tr(b, "name", lang, defaultLang) || b.name}</span>
                      <span className="text-[10px] text-white/50 shrink-0">{bc.avail}/{bc.total}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </aside>

        <main className="flex-1 overflow-auto scrollbar-thin px-6 md:px-10 py-6">
          <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-[0.3em] text-white/45" data-testid="units-crumb">
            {crumb.map((c, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="text-white/30">›</span>}
                <span className={i === crumb.length - 1 ? "text-white/70" : ""}>{c}</span>
              </React.Fragment>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {["all", "available", "reserved", "sold"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`units-status-${s}`}
                className={`text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full transition ${statusFilter === s ? "bg-white text-zinc-900" : "border border-white/15 text-white/70 hover:bg-white/5"}`}
              >
                {s === "all" ? L(lang, "all_statuses") : L(lang, s)}
              </button>
            ))}
            <div className="text-[11px] text-white/50 ml-auto">{L(lang, "units_count", list.length)}</div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {list.map((u) => {
              const b = blockById[u.group_id];
              const e = b ? etapById[b.parent_group_id] : null;
              const pathLabel = [
                e && (tr(e, "name", lang, defaultLang) || e.name),
                b && (tr(b, "name", lang, defaultLang) || b.name),
              ].filter(Boolean).join(" › ");
              return (
                <div key={u.id} className="relative group">
                <button
                  onClick={() => onPickUnit(u.id)}
                  data-testid={`unit-card-${u.id}`}
                  className="w-full text-left rounded-xl glass-light hover:bg-white/10 transition p-4"
                >
                  {pathLabel && (
                    <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1.5 truncate">{pathLabel}</div>
                  )}
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-display text-xl text-white tracking-tight">{u.code || tr(u, "name", lang, defaultLang) || L(lang, "unit")}</div>
                    <div className="flex items-center gap-1.5">
                      {unitsWithPanorama.has(u.id) && (
                        <span
                          className="text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded border border-[#C4A47C]/40 text-[#C4A47C]/85 bg-[#C4A47C]/5"
                          title={L(lang, "tour_360_available")}
                          data-testid={`unit-card-${u.id}-360-badge`}
                        >
                          360°
                        </span>
                      )}
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${statusPill(u.status)}`}>{L(lang, u.status)}</span>
                    </div>
                  </div>
                  <div className="text-[11px] text-white/60 mt-1">{L(lang, tr(u, "type", lang, defaultLang) || u.type) || tr(u, "type", lang, defaultLang) || u.type}{u.floor != null && <span className="text-white/40"> · floor {u.floor}</span>}</div>
                  <div className="mt-3 grid grid-cols-2 gap-y-1 text-[11px] text-white/70">
                    {visibleUnitFields(u, fieldDefinitions, "card").slice(0, 4).map(({ key: k, fd }) => {
                      const tv = formatFieldValue(u, fd, lang, defaultLang, project);
                      return (
                        <React.Fragment key={k}>
                          <div className="text-white/40 truncate">{fieldLabel(fd, lang, defaultLang)}</div><div className="truncate">{String(tv ?? "")}</div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </button>
                {onLocate && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onLocate(u); }}
                    data-testid={`units-list-locate-${u.id}`}
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 focus:opacity-100 transition text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-md bg-black/60 border border-[#C4A47C]/40 text-[#C4A47C] hover:bg-black/80"
                  >{L(lang, "locate")}</button>
                )}
                </div>
              );
            })}
            {list.length === 0 && <div className="col-span-full text-white/50 text-sm" data-testid="units-empty-scope">—</div>}
          </div>
        </main>
      </div>
    </div>
  );
}

function ScopeButton({ label, active, onClick, avail, total, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left px-3 py-2.5 rounded-md text-sm transition flex items-center justify-between gap-2 ${active ? "bg-white/12 text-white" : "text-white/70 hover:bg-white/5"}`}
    >
      <span className="truncate">{label}</span>
      <span className="text-[10px] text-white/50 shrink-0">{avail}/{total}</span>
    </button>
  );
}
